import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';
import { useToast } from '@/hooks/use-toast';
import { mapSupabaseError } from '@/utils/security';

export interface CounterOffer {
  id: string;
  deal_application_id: string;
  deal_id: string;
  proposed_by_profile_id: string;
  proposed_by_role: 'talent' | 'brand';
  proposed_value: number;
  proposed_terms: string | null;
  proposed_deal_type: string | null;
  proposed_deadline: string | null;
  message: string | null;
  status: 'pending' | 'accepted' | 'rejected' | 'superseded';
  responded_at: string | null;
  created_at: string;
  updated_at: string;
}

interface SubmitCounterOfferParams {
  dealApplicationId: string;
  dealId: string;
  proposedValue: number;
  proposedTerms?: string;
  proposedDealType?: string;
  proposedDeadline?: string;
  message?: string;
}

export const useCounterOffers = () => {
  const [counterOffers, setCounterOffers] = useState<CounterOffer[]>([]);
  const [loading, setLoading] = useState(false);
  const { currentUser, accountType } = useUser();
  const { toast } = useToast();

  const fetchCounterOffers = useCallback(async (dealApplicationId: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('deal_counter_offers')
        .select('*')
        .eq('deal_application_id', dealApplicationId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setCounterOffers((data || []) as CounterOffer[]);
    } catch (err: any) {
      console.error('Error fetching counter offers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const submitCounterOffer = useCallback(async (params: SubmitCounterOfferParams) => {
    if (!currentUser?.id) {
      toast({ title: 'Error', description: 'You must be logged in.', variant: 'destructive' });
      return { success: false };
    }

    const role = accountType === 'brand' ? 'brand' : 'talent';

    try {
      // Supersede any existing pending counter offers on this application
      await supabase
        .from('deal_counter_offers')
        .update({ status: 'superseded', updated_at: new Date().toISOString() })
        .eq('deal_application_id', params.dealApplicationId)
        .eq('status', 'pending');

      // Insert the new counter offer
      const { error } = await supabase
        .from('deal_counter_offers')
        .insert({
          deal_application_id: params.dealApplicationId,
          deal_id: params.dealId,
          proposed_by_profile_id: currentUser.id,
          proposed_by_role: role,
          proposed_value: params.proposedValue,
          proposed_terms: params.proposedTerms || null,
          proposed_deal_type: params.proposedDealType || null,
          proposed_deadline: params.proposedDeadline || null,
          message: params.message || null,
        });

      if (error) throw error;

      toast({ title: 'Counter Offer Sent', description: 'Your counter offer has been submitted.' });
      await fetchCounterOffers(params.dealApplicationId);
      return { success: true };
    } catch (err: any) {
      console.error('Error submitting counter offer:', err);
      toast({ title: 'Error', description: 'Failed to submit counter offer.', variant: 'destructive' });
      return { success: false };
    }
  }, [currentUser, accountType, toast, fetchCounterOffers]);

  const respondToCounterOffer = useCallback(async (
    counterId: string,
    response: 'accepted' | 'rejected',
    dealApplicationId: string
  ) => {
    try {
      // ─── ORDER MATTERS for the 'accepted' branch ──────────────────────
      // Update the deal FIRST so the funder-wallet trigger can reject
      // before any other rows mutate. If we'd marked the counter offer or
      // application 'accepted' first and the deal-update threw, those
      // rows would be left in an inconsistent state (counter offer says
      // accepted, deal still has the old value/status). For 'rejected' we
      // skip straight to the counter-offer status update below.

      if (response === 'accepted') {
        // Fetch the counter offer to get its terms
        const { data: counterOffer, error: coFetchError } = await supabase
          .from('deal_counter_offers')
          .select('*')
          .eq('id', counterId)
          .single();
        if (coFetchError) throw coFetchError;

        if (counterOffer) {
          // Fetch the application to get talent_profile_id
          const { data: application } = await supabase
            .from('deal_applications')
            .select('talent_profile_id, deal_id')
            .eq('id', dealApplicationId)
            .single();

          // Update the deal with the accepted terms + activate + assign talent.
          // Wallet-funding trigger fires here; if it throws nothing else is
          // mutated yet.
          const updates: any = {
            deal_value: counterOffer.proposed_value,
            status: 'active',
          };
          if (application?.talent_profile_id) updates.talent_profile_id = application.talent_profile_id;
          if (counterOffer.proposed_deal_type) updates.deal_type = counterOffer.proposed_deal_type;
          if (counterOffer.proposed_deadline) updates.estimated_completion_date = counterOffer.proposed_deadline;

          const { error: dealUpdateError } = await supabase
            .from('deals')
            .update(updates)
            .eq('id', counterOffer.deal_id);
          if (dealUpdateError) throw dealUpdateError;

          // Now flip the counter offer + applications to their final states.
          const { error: coStatusError } = await supabase
            .from('deal_counter_offers')
            .update({
              status: 'accepted',
              responded_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            })
            .eq('id', counterId);
          if (coStatusError) throw coStatusError;

          const { error: appAcceptError } = await supabase
            .from('deal_applications')
            .update({ status: 'accepted', reviewed_at: new Date().toISOString() })
            .eq('id', dealApplicationId);
          if (appAcceptError) throw appAcceptError;

          const { error: rejectOthersError } = await supabase
            .from('deal_applications')
            .update({
              status: 'rejected',
              reviewed_at: new Date().toISOString(),
              notes: 'Deal was awarded to another applicant',
            })
            .eq('deal_id', counterOffer.deal_id)
            .eq('status', 'pending')
            .neq('id', dealApplicationId);
          if (rejectOthersError) throw rejectOthersError;
        }
      } else {
        // Plain rejection — no deal mutation, just flip the counter status.
        const { error } = await supabase
          .from('deal_counter_offers')
          .update({
            status: 'rejected',
            responded_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq('id', counterId);
        if (error) throw error;
      }

      toast({
        title: response === 'accepted' ? 'Counter Offer Accepted' : 'Counter Offer Rejected',
        description: response === 'accepted'
          ? 'The deal has been updated with the agreed terms.'
          : 'The counter offer has been declined.',
      });

      await fetchCounterOffers(dealApplicationId);
      return { success: true };
    } catch (err: any) {
      // Surface user-safe DB errors verbatim (e.g. wallet-funding guard
      // reject when accepting a counter offer pushes the deal value above
      // the brand's wallet balance).
      toast({
        title: 'Error',
        description: mapSupabaseError(err, 'Failed to respond to counter offer.'),
        variant: 'destructive',
      });
      return { success: false };
    }
  }, [toast, fetchCounterOffers]);

  const getLatestPendingOffer = useCallback(() => {
    return counterOffers.find(co => co.status === 'pending') || null;
  }, [counterOffers]);

  return {
    counterOffers,
    loading,
    fetchCounterOffers,
    submitCounterOffer,
    respondToCounterOffer,
    getLatestPendingOffer,
  };
};
